import { Tagged } from 'type-fest';

import { ImageUrl } from './shared';

export type PlatformAppLoader = 'templates' | 'instances';

type TemplateLink = { text: string; url: string };

type TemplateAsset = { type: 'image' | 'video'; url: string };

export enum SchemaField {
  ClusterName = 'clusterName',
  Checkbox = 'enabled',
  Grpc = 'grcp',
}

export type AnyOfEntry = {
  $ref?: string;
} & {
  type: SchemaTypeValue;
};

export type AnyOf = AnyOfEntry[];

export type SchemaTypeValue =
  | 'string'
  | 'object'
  | 'enum'
  | 'array'
  | 'boolean'
  | 'integer'
  | 'number'
  | 'float'
  | 'null';

export type SchemaType = {
  type: SchemaTypeValue;
};

export type JSONSchema = SchemaProperties.Properties & {
  type?: string;
  properties?: Record<string, JSONSchema>;
  required?: string[];
  default?: any;
  enum?: string[];
  $defs: Record<string, JSONSchema>;
  $ref: string;
  items?: JSONSchema;
  anyOf: AnyOf;
};

export namespace SchemaValidation {
  export type Required = string[];

  export type Schema = {
    required?: Required;
    anyOf: AnyOf;
  };
}

export namespace SchemaProperties {
  export type Properties = {
    title: string;
    description?: string;
    default?: any;
    'x-title'?: string;
    'x-description'?: string;
    'x-logo-url'?: string;
    'x-meta-type'?: PlatformApp.InputMetaType;
    logoUrl?: string;
    type: SchemaType;
  };

  export type Reference = {
    title: string;
    descripton?: string;
    $ref?: string;
    anyOf?: AnyOfEntry[];
    type: SchemaTypeValue;
  };

  export type Model = Properties | Reference;
}

export namespace SchemaDefinition {
  export type DefinitionName = Tagged<'SchemDefinitionName', string>;
  export type ComponentName = Tagged<'SchemaDefinitionComponentName', string>;

  export type Model = SchemaValidation.Schema & {
    title: string;
    properties: Record<ComponentName, SchemaProperties.Model>;
  };

  export type Definitions = Record<DefinitionName, Model>;
}

export namespace PlatformApp {
  export type InputTypeParams = Record<string, string | number>;

  export enum InputMetaType {
    Integration = 'integration',
    Inline = 'inline',
  }

  export type InputType = JSONSchema;

  export type InputMetadata = {
    title: string;
    description?: string;
    logoUrl?: string;
    metaType?: InputMetaType;
  };

  export type OutputType = InputType;

  export type Template = {
    name: string;
    title: string;
    shortDescription: string;
    description: string;
    version: string;
    logo: ImageUrl;
    documentationUrls: TemplateLink[];
    externalUrls: TemplateLink[];
    /**
     * todo: make it as string date format
     */
    pubDate: string;
    assets: TemplateAsset[];
    tags: string[];
    gitUrl: string;
    input: InputType;
    output: InputType;
  };

  export type Instance = PlatformAppInstance.Model;
}

export namespace PlatformAppUiSchema {
  export type NodeProperties = {
    title?: string;
    description?: string;
    logoUrl?: string;
    metaType?: 'integration' | 'inline';
    typeName?: string;
  };

  export enum Type {
    Field = 'field',
    Container = 'container',
    AnyOf = 'anyOf',
    Array = 'array',
  }

  export type FieldProperties = {
    name: string;
    title: string;
    description?: string;
    type: SchemaTypeValue;
    const?: string;
    /**
     * Enum options
     */
    options?: string[];
    items?: {
      type: Extract<SchemaTypeValue, 'string' | 'number' | 'float' | 'integer'>;
    };
    required?: boolean;
    default?: any;
  };

  export type Container = NodeProperties & {
    type: Type.Container;
    name: string;
    children: Model[];
  };

  export type Field = NodeProperties & {
    type: Type.Field;
    name: string;
    properties: FieldProperties;
  };

  export type AnyOf = NodeProperties & {
    type: Type.AnyOf;
    name: string;
    children: Model[];
    default?: unknown;
  };

  export type Array = NodeProperties & {
    type: Type.Array;
    name: string;
    default?: any;
    children: Model[];
  };

  export type Model = Container | Field | AnyOf | Array;

  export type ModelWithChildren = Container | AnyOf;

  export type ModelWithoutChildren = Exclude<Model, ModelWithChildren>;
}

export namespace PlatformAppInstance {
  export type Model = {
    id: string;
    name: string;
    namespace: string;
    creator: string;
    appType: string;
    displayName: string;
    templateName: string;
    templateVersion: string;
    projectName: string;
    orgName: string;
    state: string;
    createdAt: string;
  };
}
